import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, FileText, Check, X, MessageSquare, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { euros } from "@/constants/business_status";

const portal = axios.create({ baseURL: API });

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null); // {quoteId, type}
  const [form, setForm] = useState({ name: "", consent: false, comment: "" });
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    portal.get(`/portal/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide ou expiré"));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="min-h-screen bg-graylight flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md" data-testid="portal-error">
        <h1 className="font-heading font-bold text-xl text-navy mb-2">Lien indisponible</h1>
        <p className="text-sm text-muted-foreground">{String(error)}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;

  const submitDecision = async () => {
    const { quoteId, type } = decision;
    try {
      if (type === "change") {
        await portal.post(`/portal/${token}/quotes/${quoteId}/request-change`, { comment: form.comment });
        toast.success("Demande de modification transmise");
      } else {
        await portal.post(`/portal/${token}/quotes/${quoteId}/${type}`, {
          declared_name: form.name, consent: form.consent, comment: form.comment });
        toast.success(type === "accept" ? "Devis accepté — merci !" : "Décision enregistrée");
      }
      setDecision(null); load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Erreur")); }
  };

  const uploadProof = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await portal.post(`/portal/${token}/upload-proof`, fd);
      toast.success("Justificatif transmis — il sera vérifié par notre équipe");
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Erreur d'envoi")); }
  };

  const copyText = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    await portal.post(`/portal/${token}/message`, { text: message });
    toast.success("Message envoyé à votre conseiller");
    setMessage("");
  };

  const c = data.case;
  return (
    <div className="min-h-screen bg-graylight">
      <header className="bg-navy px-5 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="font-heading font-bold text-white">Express Delivery</span>
          <span className="text-white/70 text-sm">Suivi de dossier</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="portal-case-card">
          <div className="text-xs uppercase tracking-widest text-orange font-bold mb-1">Votre dossier</div>
          <h1 className="font-heading font-extrabold text-xl text-navy">
            {data.contact_first_name ? `Bonjour ${data.contact_first_name} — ` : ""}{c.case_number}
          </h1>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <span className="bg-navy/5 text-navy font-semibold rounded-full px-3 py-1" data-testid="portal-case-status">{c.status_label}</span>
            {c.service_type && <span className="bg-navy/5 text-navy rounded-full px-3 py-1">{c.service_type}</span>}
            {c.requested_date && <span className="bg-navy/5 text-navy rounded-full px-3 py-1">Date souhaitée : {c.requested_date}</span>}
          </div>
          {(c.origin_address?.city || c.destination_address?.city) && (
            <div className="text-sm text-muted-foreground mt-2">
              {c.origin_address?.city || "—"} → {c.destination_address?.city || "—"}
            </div>
          )}
          {data.mission && (
            <div className="mt-3 bg-green-50 text-green-800 rounded-xl px-4 py-2.5 text-sm" data-testid="portal-mission">
              Intervention {data.mission.status_label?.toLowerCase()} le <strong>{data.mission.date}</strong>{data.mission.start_time ? ` à ${data.mission.start_time}` : ""}
            </div>
          )}
        </div>

        {data.quotes.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="portal-quotes">
            <div className="text-xs uppercase tracking-widest text-orange font-bold mb-3">Vos devis</div>
            {data.quotes.map((q) => (
              <div key={q.id} className="border rounded-xl p-4 mb-3 last:mb-0" data-testid={`portal-quote-${q.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="mr-auto">
                    <div className="font-bold text-navy">{q.quote_number} — Version {q.current_version}</div>
                    <div className="text-sm text-muted-foreground">{euros(q.totals?.total_ttc_cents)} TTC {q.valid_until ? `• valable jusqu'au ${q.valid_until}` : ""}</div>
                  </div>
                  <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1">{data.quote_labels[q.status] || q.status}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {q.versions[0] && (
                    <a href={`${API}/portal/${token}/quote-versions/${q.versions[0].id}/pdf`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="rounded-xl" data-testid={`portal-quote-pdf-${q.id}`}><FileText className="w-4 h-4 mr-1" />Télécharger le PDF</Button>
                    </a>
                  )}
                  {["sent", "viewed", "change_requested"].includes(q.status) && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-xl" data-testid={`portal-quote-accept-${q.id}`}
                        onClick={() => { setDecision({ quoteId: q.id, type: "accept" }); setForm({ name: "", consent: false, comment: "" }); }}>
                        <Check className="w-4 h-4 mr-1" />Accepter</Button>
                      <Button size="sm" variant="outline" className="rounded-xl" data-testid={`portal-quote-change-${q.id}`}
                        onClick={() => { setDecision({ quoteId: q.id, type: "change" }); setForm({ name: "", consent: false, comment: "" }); }}>
                        Demander une modification</Button>
                      <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200" data-testid={`portal-quote-refuse-${q.id}`}
                        onClick={() => { setDecision({ quoteId: q.id, type: "refuse" }); setForm({ name: "", consent: false, comment: "" }); }}>
                        <X className="w-4 h-4 mr-1" />Refuser</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {decision && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDecision(null)}>
            <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="portal-decision-modal">
              <h2 className="font-bold text-navy mb-3">
                {decision.type === "accept" ? "Accepter le devis" : decision.type === "refuse" ? "Refuser le devis" : "Demander une modification"}
              </h2>
              {decision.type !== "change" && (
                <>
                  <label className="text-xs font-semibold text-navy">Votre nom complet</label>
                  <Input data-testid="portal-decision-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="mb-2 bg-white" />
                </>
              )}
              <label className="text-xs font-semibold text-navy">Commentaire {decision.type === "change" ? "(précisez la modification souhaitée)" : "(facultatif)"}</label>
              <Textarea data-testid="portal-decision-comment" value={form.comment} onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))} rows={3} className="mb-3 bg-white" />
              {decision.type === "accept" && (
                <label className="flex items-start gap-2 text-sm mb-3">
                  <input data-testid="portal-decision-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm((p) => ({ ...p, consent: e.target.checked }))} className="mt-0.5" />
                  Je confirme accepter cette proposition pour le montant indiqué. Cette validation vaut accord de commande.
                </label>
              )}
              <div className="flex gap-2">
                <Button data-testid="portal-decision-submit" onClick={submitDecision} className="bg-navy text-white rounded-xl flex-1"
                  disabled={decision.type === "accept" && (!form.name || !form.consent)}>Confirmer</Button>
                <Button variant="outline" className="rounded-xl" onClick={() => setDecision(null)}>Annuler</Button>
              </div>
            </div>
          </div>
        )}

        {data.payments && (
          <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="portal-payments">
            <div className="text-xs uppercase tracking-widest text-orange font-bold mb-3">Règlement</div>
            <div className="grid grid-cols-3 gap-3 text-center mb-3">
              <div><div className="text-lg font-extrabold text-navy">{euros(data.payments.expected_cents)}</div><div className="text-xs text-muted-foreground">Montant prévu</div></div>
              <div><div className="text-lg font-extrabold text-green-700">{euros(data.payments.recorded_cents)}</div><div className="text-xs text-muted-foreground">Enregistré</div></div>
              <div><div className="text-lg font-extrabold text-orange">{euros(data.payments.remaining_cents)}</div><div className="text-xs text-muted-foreground">Reste à régler</div></div>
            </div>
            {data.payments.instructions && <p className="text-sm mb-1">{data.payments.instructions}</p>}
            {data.payments.bank ? (
              <div className="bg-graylight rounded-xl p-4 mb-3" data-testid="portal-bank-block">
                <div className="text-xs uppercase tracking-wider text-navy font-bold mb-2">Règlement par virement bancaire</div>
                <div className="text-sm space-y-1">
                  {data.payments.bank.beneficiary && <div><span className="text-muted-foreground">Bénéficiaire :</span> <strong data-testid="portal-bank-beneficiary">{data.payments.bank.beneficiary}</strong></div>}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-muted-foreground">IBAN :</span>
                    <span className="font-mono" data-testid="portal-bank-iban">{data.payments.bank.iban_display}</span>
                    <button data-testid="portal-copy-iban" onClick={() => copyText(data.payments.bank.iban, "IBAN copié")}
                      className="text-xs font-semibold text-orange underline">Copier l'IBAN</button>
                  </div>
                  {data.payments.bank.bic && <div><span className="text-muted-foreground">BIC :</span> <span className="font-mono" data-testid="portal-bank-bic">{data.payments.bank.bic}</span></div>}
                  {data.payments.reference && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-muted-foreground">Référence à indiquer :</span>
                      <strong data-testid="portal-bank-reference">{data.payments.reference}</strong>
                      <button data-testid="portal-copy-reference" onClick={() => copyText(data.payments.reference, "Référence copiée")}
                        className="text-xs font-semibold text-orange underline">Copier la référence</button>
                    </div>
                  )}
                  {data.payments.next_due_date && <div><span className="text-muted-foreground">Échéance :</span> {data.payments.next_due_date}</div>}
                  {data.payments.bank.instructions && <p className="text-xs text-muted-foreground pt-1">{data.payments.bank.instructions}</p>}
                </div>
                <p className="text-xs text-muted-foreground mt-3" data-testid="portal-proof-hint">
                  Après avoir effectué votre virement depuis votre banque, vous pouvez déposer votre justificatif afin d'en faciliter la vérification.
                </p>
              </div>
            ) : (
              <>
                {data.payments.rib && <p className="text-sm font-mono bg-graylight rounded-lg p-2 mb-1">{data.payments.rib}</p>}
                {data.payments.reference && <p className="text-xs text-muted-foreground mb-2">Référence à indiquer : <strong>{data.payments.reference}</strong></p>}
              </>
            )}
            {data.payments.pending_verification > 0 && <p className="text-xs text-amber-700 mb-2">Un justificatif est en cours de vérification.</p>}
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer bg-graylight rounded-xl px-4 py-2.5">
              <Upload className="w-4 h-4" /> Déposer un justificatif de paiement
              <input data-testid="portal-upload-proof" type="file" accept="image/*,.pdf" className="hidden"
                onChange={(e) => e.target.files[0] && uploadProof(e.target.files[0])} />
            </label>
          </div>
        )}

        {(data.documents.length > 0 || data.invoices.length > 0) && (
          <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="portal-documents">
            <div className="text-xs uppercase tracking-widest text-orange font-bold mb-3">Vos documents</div>
            {data.invoices.map((i) => (
              <a key={i.id} href={`${API}/portal/${token}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 py-2 border-b last:border-0 text-sm text-navy font-medium">
                <FileText className="w-4 h-4 text-orange" /> {i.is_credit_note ? "Avoir" : "Facture"} {i.invoice_number} — {euros(i.totals?.total_ttc_cents)}
              </a>
            ))}
            {data.documents.map((d) => (
              <a key={d.id} href={`${API}/portal/${token}/documents/${d.id}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 py-2 border-b last:border-0 text-sm text-navy font-medium">
                <FileText className="w-4 h-4 text-navy" /> {d.name}
              </a>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="portal-contact">
          <div className="text-xs uppercase tracking-widest text-orange font-bold mb-3">Une question ?</div>
          <div className="flex gap-2">
            <Input data-testid="portal-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Écrivez un message à votre conseiller…" className="bg-white" />
            <Button data-testid="portal-message-send" onClick={sendMessage} className="bg-navy text-white rounded-xl"><MessageSquare className="w-4 h-4" /></Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Ou par téléphone : <a className="font-semibold text-navy" href={`tel:${data.company.phone}`}>{data.company.phone}</a></p>
        </div>
      </main>
    </div>
  );
}
